import {classes, merge_deep, pending} from '@bitstillery/common/lib/utils'
import {FieldText, Button, Link, Icon, DataCard} from '@bitstillery/common/components'
import {MithrilComponent} from '@bitstillery/mithril'
import {countries, european_union_countries} from '@bitstillery/common/lib/countries'
import {modelref_adapter} from '@bitstillery/common/lib/store'
import {state} from '@bitstillery/mithril'
import {$t, api, notifier} from '@bitstillery/common/app'
import type {ValidateRelationViesResponse} from '@bitstillery/common/api/fact2server'
import m from '@bitstillery/mithril'

import type {Field, FieldTextAttrs} from '@bitstillery/common/types/field'

interface FieldVatIdAttrs {
    className?: Field['className']
    country_code: string
    description?: string
    disabled?: Field['disabled']
    model?: FieldTextAttrs['model']
    validation?: Field['validation']
}

/** No answer from VIES yet: nothing about the vat-id is known, so it doesn't read as valid either. */
function empty_vies_result(): ValidateRelationViesResponse {
    return {address: null, country_code: null, name: null, valid: false, vat_number: null}
}

interface FieldVatIdData {
    country_code: string
    loading: boolean
    vies_available: boolean
    vies_error: string | undefined
    vies_result: ValidateRelationViesResponse
    vies_verified: boolean
}

export class FieldVatId extends MithrilComponent<FieldVatIdAttrs> {
    data: FieldVatIdData = state({
        country_code: pending<string>(),
        loading: false,
        vies_result: empty_vies_result(),
        vies_verified: false,
        vies_available: false,
        vies_error: undefined,
    })

    override async oninit(vn: m.Vnode<FieldVatIdAttrs>) {
        this.data.country_code = vn.attrs.country_code
        await this.is_vies_available()
    }

    async is_vies_available() {
        const {success} = await api.head('discover/relations/vat-id/validate')
        this.data.vies_available = success
    }

    view(vn: m.Vnode<FieldVatIdAttrs>) {
        this.data.country_code = vn.attrs.country_code
        const vat_label = $t('validation.vat_id.label', {country: countries[vn.attrs.country_code]})

        const validation = vn.attrs.validation
        if (validation) {
            validation.description = vat_label
        }
        const invalid = validation ? validation._invalid : false
        const disabled = vn.attrs.disabled || this.data.loading
        const {model_value} = modelref_adapter(vn.attrs.model)

        return (
            <div
                className={classes('c-field-vat-id field-compound', vn.attrs.className, {
                    disabled: disabled,
                    invalid: validation && invalid && validation.dirty,
                    valid: validation && !invalid && validation.dirty,
                })}
            >
                <div className='input-form'>
                    <FieldText
                        description={vn.attrs.description}
                        disabled={disabled}
                        help={(() => {
                            if (this.data.vies_verified) {
                                return null
                            }
                            if (european_union_countries.includes(this.data.country_code)) {
                                return $t('validation.vat_id.help_validate', {country: this.data.country_code})
                            }
                            return $t('validation.vat_id.help_disabled', {country: this.data.country_code})
                        })()}
                        label={vat_label}
                        model={vn.attrs.model}
                        onafterupdate={() => {
                            this.data.vies_verified = false
                        }}
                        validation={validation}
                    />
                    <Button
                        disabled={disabled || Boolean(invalid)}
                        loading={this.data.loading}
                        icon={this.data.vies_available ? 'checkboxMarked' : 'question'}
                        onclick={async (e) => {
                            e.preventDefault()
                            this.data.loading = true
                            this.data.vies_error = undefined
                            const {status_code, result, error_result} = await api.post(
                                'discover/relations/vat-id/validate',
                                {
                                    country_code: vn.attrs.country_code,
                                    vat_id: typeof model_value === 'string' ? model_value : '',
                                },
                                true,
                            )
                            if (status_code > 299) {
                                // A 5xx carries no error body; api already notified the server error.
                                const detail = error_result?.detail
                                notifier.notify(
                                    detail ? `Cannot validate the VAT number: ${detail}` : 'Cannot validate the VAT number',
                                    'warning',
                                )
                                this.data.vies_error = detail
                                this.data.loading = false
                                return
                            }
                            merge_deep(this.data.vies_result, result)
                            this.data.vies_verified = true
                            this.data.loading = false
                        }}
                        text={$t('validation.vat_id.verify')}
                        tip={() =>
                            `Validate the vat-id at the european union. API status: ${this.data.vies_available ? 'Available' : 'Not available'}`
                        }
                        type='info'
                    />
                </div>
                <div>
                    Visit the EU Site for validation:
                    <Link href='https://ec.europa.eu/taxation_customs/vies/#/vat-validation'>
                        {' '}
                        Link <Icon name='launch' size='s' />
                    </Link>
                </div>
                <DataCard
                    model={{
                        data: [
                            !this.data.vies_verified
                                ? {
                                      label: 'Verification done',
                                      value: <Icon name='question' type='danger' tip='VAT id is not verified recently' />,
                                  }
                                : null,
                            this.data.vies_verified
                                ? {
                                      label: 'Valid',
                                      value: <Icon name='checked' type={this.data.vies_result.valid ? 'success' : 'brand'} />,
                                  }
                                : null,
                            this.data.vies_error
                                ? {
                                      label: 'Error',
                                      value: <Icon name='bug' type='danger' tip={this.data.vies_error} />,
                                  }
                                : null,
                            this.data.vies_verified
                                ? {
                                      label: 'Vies response',
                                      value: `${this.data.vies_result.country_code ?? ''}${this.data.vies_result.vat_number ?? ''}`,
                                  }
                                : null,
                            this.data.vies_verified && this.data.vies_result.address !== '---'
                                ? {
                                      label: 'Vies name / address',
                                      value: `${this.data.vies_result.name ?? '-'}/${this.data.vies_result.address ?? '-'}`,
                                  }
                                : null,
                        ],
                    }}
                />
            </div>
        )
    }
}
